import React, { lazy, Suspense, useEffect, useState, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { MoreProjectsProvider } from "./moreProjectsContext";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

const MoreProjects = lazy(() => import("./Screens/MoreProjects"));
const HomeScreen = lazy(() => import("./Screens/HomeScreen"));
const Header = lazy(() => import("./Components/Header"));

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: theme.palette.primary.main,
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const BetaPreviewLink = styled("a")(({ theme }) => ({
  position: "fixed",
  top: 12,
  left: 12,
  zIndex: theme.zIndex.appBar + 1,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "6px 10px",
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: 6,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[2],
  fontSize: "0.75rem",
  fontWeight: 700,
  lineHeight: 1,
  textDecoration: "none",
  transition: "background-color 150ms ease, color 150ms ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

function App() {
  const homeRef = useRef(null);
  const projectsRef = useRef(null);
  const articlesRef = useRef(null);
  const aboutRef = useRef(null);
  const [darkState, setDarkState] = useState("dark");

  const theme = createTheme({
    palette: {
      mode: darkState,
      primary: {
        // main: darkState === "dark" ? "#ff8600" : "#5ec462",
        main: darkState === "dark" ? "#5ec462" : "#ff8600",
      },
      secondary: {
        main: darkState === "dark" ? "#dee4e7" : "#000" ,
      },
      background: {
        default: darkState === "dark" ? "linear-gradient(180deg, #1a237e 0%, #0d47a1 100%)" : "#dee4e7",
      },
    },
  });

  const toggleDarkState = () => {
    if (darkState === "light") {
      setDarkState("dark");
      localStorage.setItem("darkState", "dark");
    } else {
      setDarkState("light");
      localStorage.setItem("darkState", "light");
    }
  };

  useEffect(() => {
    const existingPreference = localStorage.getItem("darkState");
    if (existingPreference) {
      existingPreference === "light"
        ? setDarkState("light")
        : setDarkState("dark");
    } else {
      setDarkState("dark");
      localStorage.setItem("darkState", "dark");
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BetaPreviewLink
        href="https://redesign2026--veereshr.netlify.app/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View the 2026 beta redesign in a new tab"
      >
        View 2026 beta
        <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
      </BetaPreviewLink>
      <Suspense
        fallback={
          <StyledBackdrop open={true}>
            <StyledCircularProgress />
          </StyledBackdrop>
        }
      >
        <Router>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Header
                    refProp={[homeRef, projectsRef, articlesRef, aboutRef]}
                  />
                  <HomeScreen
                    refProp={[homeRef, projectsRef, articlesRef, aboutRef]}
                    darkState={darkState}
                    toggleDarkState={toggleDarkState}
                  />
                </>
              }
            />
            <Route
              path="/moreprojects"
              element={
                <MoreProjectsProvider>
                  <MoreProjects
                    darkState={darkState}
                    toggleDarkState={toggleDarkState}
                  />
                </MoreProjectsProvider>
              }
            />
          </Routes>
        </Router>
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
